import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { WizardStep } from '@/components/postgrid/wizard/WizardStep';
import { Field, FieldGroup, Label } from '@/components/ui/catalyst/fieldset';
import { Input } from '@/components/ui/catalyst/input';
import { Text } from '@/components/ui/catalyst/text';
import { Textarea } from '@/components/ui/catalyst/textarea';

const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;

const formSchema = z.object({
  phoneNumber: z
    .string()
    .min(1, 'Phone number is required')
    .regex(
      phoneRegex,
      'Please enter a valid phone number (digits only, optional + prefix)',
    )
    .transform((value) => value.replace(/[^\d+]/g, '')),
  coverTo: z.string().optional().default(''),
  coverFrom: z.string().optional().default(''),
  coverMessage: z.string().optional().default(''),
});

type FormValues = z.infer<typeof formSchema>;

interface WizardData {
  recipient_number?: string;
  cover_page_to?: string;
  cover_page_from?: string;
  cover_page_message?: string;
  [key: string]: unknown;
}

interface FaxRecipientStepProps {
  wizardData?: WizardData;
  updateStepData?: (data: Partial<WizardData>) => void;
  markStepComplete?: (isValid: boolean) => void;
  className?: string;
}

export const FaxRecipientStep: React.FC<FaxRecipientStepProps> = ({
  wizardData = {},
  updateStepData,
  markStepComplete,
  className = '',
}) => {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      phoneNumber: wizardData.recipient_number || '',
      coverTo: (wizardData.cover_page_to as string) || '',
      coverFrom: (wizardData.cover_page_from as string) || '',
      coverMessage: (wizardData.cover_page_message as string) || '',
    },
    mode: 'onChange',
  });

  const { watch, formState } = form;
  const phoneNumber = watch('phoneNumber');
  const coverTo = watch('coverTo');
  const coverFrom = watch('coverFrom');
  const coverMessage = watch('coverMessage');

  // Update wizard data and step validity when form changes
  useEffect(() => {
    const isValid = formState.isValid && phoneNumber.trim() !== '';
    updateStepData?.({
      recipient_number: phoneNumber,
      cover_page_to: coverTo,
      cover_page_from: coverFrom,
      cover_page_message: coverMessage,
    });
    markStepComplete?.(isValid);
  }, [
    phoneNumber,
    coverTo,
    coverFrom,
    coverMessage,
    formState.isValid,
    updateStepData,
    markStepComplete,
  ]);

  return (
    <WizardStep
      title="Recipient"
      description="Enter the fax number where you want to send the document."
      isValid={formState.isValid && phoneNumber.trim() !== ''}
      markStepComplete={markStepComplete}
      className={className}
    >
      <FieldGroup>
        <Field>
          <Label htmlFor="phoneNumber">Fax Number</Label>
          <Input
            id="phoneNumber"
            type="tel"
            placeholder="e.g., +15551234567 or 5551234567"
            {...form.register('phoneNumber')}
            invalid={!!formState.errors.phoneNumber}
          />
          {formState.errors.phoneNumber && (
            <Text className="mt-1 text-sm text-red-600">
              {formState.errors.phoneNumber.message}
            </Text>
          )}
          <Text className="mt-1 text-sm text-gray-500">
            Enter a valid fax number with country code (e.g., +1 for US
            numbers).
          </Text>
        </Field>
        <div className="border-t border-gray-200 pt-4 mt-4" />
        <Field>
          <Label>Fax Cover Sheet (optional)</Label>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="coverTo" className="text-xs text-gray-600">
                To
              </Label>
              <Input
                id="coverTo"
                type="text"
                placeholder="e.g., Records Department"
                {...form.register('coverTo')}
              />
            </div>
            <div>
              <Label htmlFor="coverFrom" className="text-xs text-gray-600">
                From
              </Label>
              <Input
                id="coverFrom"
                type="text"
                placeholder="e.g., Your Name"
                {...form.register('coverFrom')}
              />
            </div>
          </div>
          <div className="mt-3">
            <Label htmlFor="coverMessage" className="text-xs text-gray-600">
              Message
            </Label>
            <Textarea
              id="coverMessage"
              rows={4}
              placeholder="Message to include on cover page"
              {...form.register('coverMessage')}
            />
            <Text className="mt-1 text-xs text-gray-500">
              These details will be used to generate a cover page.
            </Text>
          </div>
        </Field>
      </FieldGroup>
    </WizardStep>
  );
};
